import React from 'react';

import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  BorderColor as BorderColorIcon,
  Delete as DeleteFilledIcon,
  Redo as RedoIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material';

import { STATUSES_FILTER_OPTIONS } from '@clearverify/constants';
import { formatPhoneNumber, parseNullableValue } from '@clearverify/utils';

import { PeoStaffInfoFragment, UserStatus, useResendInviteToPeoStaffMutation } from 'src/api';
import { ClickableLabel, UserStatusChip } from 'src/components/common';
import { DemoPrivacyText } from 'src/components/demoPrivacyMode';
import {
  Table,
  TableColumn,
  TableFilter,
  TableRowAction,
  TableThemes,
  TableTopAction,
} from 'src/components/table';
import { ModalId } from 'src/constants/modals';
import { PEO_STAFF_ROLE_LABEL_MAP } from 'src/constants/peo';
import { useNotification } from 'src/hooks/app';
import { useFilterStorage, useSearch } from 'src/hooks/common';
import { useModal, useModalV2 } from 'src/hooks/shared';
import { useCurrentUserPermissions } from 'src/hooks/user';
import { MfaManagementModal } from 'src/modals/auth';
import { ResendInviteModalArgs } from 'src/modals/invite';
import { DeletePeoStaffConfirmationModal } from 'src/modals/peoStaff';
import { PeoStaffViewDetailsModalArgs } from 'src/modals/peoStaff/PeoStaffViewDetailsModal/types';
import { PaginationContextValue } from 'src/providers/PaginationProvider';

export interface PeoStaffTableProps {
  peoStaffList?: PeoStaffInfoFragment[];
  peoStaffCount?: number;
  topActions?: Array<TableTopAction>;
  pagination: PaginationContextValue;
  onRefresh: () => Promise<void>;
  loading: boolean;
}

export const PeoStaffTable: React.FC<PeoStaffTableProps> = ({
  peoStaffList = [],
  peoStaffCount = 0,
  topActions,
  pagination,
  onRefresh,
  loading,
}) => {
  const { createApolloContext } = useNotification();

  const theme = useTheme();

  const { openModal } = useModal();
  const { openModal: openModalV2 } = useModalV2();

  const { setSearchValue, searchValue } = useSearch();
  const { setSortOption } = useFilterStorage();

  const { hasPlatformRoles, isPlatformAdmin, isPeoAdmin, isPeoPayrollProviderUser } =
    useCurrentUserPermissions();

  const [resendInviteToPeoStaff] = useResendInviteToPeoStaffMutation({
    context: createApolloContext({
      successMessage: 'Invite has been resent successfully',
    }),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies:useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue]);

  const rowActions = React.useMemo(() => {
    const actions: Array<TableRowAction<PeoStaffInfoFragment>> = [
      {
        actionName: 'View Details',
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        onAction: peoStaff => {
          openModal(ModalId.PeoStaffViewDetails, {
            peoStaffId: peoStaff.id,
          } satisfies PeoStaffViewDetailsModalArgs);
        },
      },
    ];

    if (hasPlatformRoles) {
      actions.push({
        startIcon: <AdminPanelSettingsIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'MFA Management',
        onAction: staff => {
          openModalV2(MfaManagementModal, { id: ModalId.MfaManagement, userId: staff.user.id });
        },
      });
    }

    if (isPlatformAdmin) {
      actions.push({
        startIcon: <DeleteFilledIcon sx={{ margin: '0 10px 0 0', color: 'error.main' }} />,
        actionName: 'Delete Staff',
        style: { color: theme.palette.error.main },
        onAction: staff => {
          openModalV2(DeletePeoStaffConfirmationModal, {
            id: ModalId.DeletePeoStaffConfirmationModal,
            peoStaffId: staff.id,
          });
        },
      });
    }

    if (hasPlatformRoles || isPeoAdmin || isPeoPayrollProviderUser) {
      actions.push({
        startIcon: <RedoIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'Resend Invite',
        hidden: staff => staff.user.status !== UserStatus.Pending,
        onAction: staff => {
          const args: ResendInviteModalArgs = {
            toStaff: 'PEO',
            staffEmail: staff.user.email,
            onSubmit: async () => {
              const { data } = await resendInviteToPeoStaff({
                variables: { peoStaffId: staff.id },
              });
              return data?.resendInviteToPeoStaff;
            },
          };

          openModal(ModalId.ResendInvite, args);
        },
      });
    }

    return actions;
  }, [
    hasPlatformRoles,
    isPlatformAdmin,
    isPeoAdmin,
    isPeoPayrollProviderUser,
    openModal,
    openModalV2,
    resendInviteToPeoStaff,
    theme,
  ]);

  return (
    <Table
      dataSource={peoStaffList}
      rowCount={peoStaffCount}
      columns={COLUMNS}
      pagination={pagination}
      onSearch={setSearchValue}
      loading={loading}
      disabled={loading}
      topActions={topActions}
      rowActions={rowActions}
      filters={filters}
      tableTheme={TableThemes.withBorderWithoutProfileIcon}
      onRefresh={onRefresh}
      onSort={setSortOption}
    />
  );
};

const COLUMNS: TableColumn<PeoStaffInfoFragment>[] = [
  {
    title: 'Full Name',
    key: 'user.firstName',
    sortable: true,
    render: (_valueByDataPath, peoStaff, { rowActions }) => {
      return (
        <ClickableLabel
          type="button"
          onClick={() => rowActions?.[0]?.onAction(peoStaff)}
          label={peoStaff.user.fullName}
          labelPostfix={PEO_STAFF_ROLE_LABEL_MAP[peoStaff.role]}
          logoUrl={peoStaff.user.avatar?.downloadUrl}
        />
      );
    },
  },
  {
    title: 'Phone Number',
    key: 'user.phoneNumber',
    render: (_valueByDataPath, peoStaff) => (
      <DemoPrivacyText>
        {parseNullableValue(formatPhoneNumber(peoStaff.user.phoneNumber))}
      </DemoPrivacyText>
    ),
  },
  {
    title: 'Email',
    key: 'user.email',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, staff) => (
      <DemoPrivacyText>{parseNullableValue(staff.user.email)}</DemoPrivacyText>
    ),
  },
  {
    title: 'Status',
    key: 'user.status',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, staff) => <UserStatusChip userStatus={staff.user.status} />,
  },
];

const filters: TableFilter[] = [
  {
    id: 'status',
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: STATUSES_FILTER_OPTIONS,
    },
  },
];
